import { useEffect, useRef } from "react";

const steps = [
  {
    num: "01",
    icon: "ri-user-star-line",
    label: "선발",
    sublabel: "Audition",
    color: "#A855F7",
    glow: "rgba(168,85,247,0.3)",
    description: "아르트레너 진단 결과와 포트폴리오를 검토합니다. 빌더팀과의 1:1 인터뷰를 통해 최적 플랜을 확정합니다.",
    detail: "온라인 지원 → 서류 검토 → 화상 인터뷰",
  },
  {
    num: "02",
    icon: "ri-draft-line",
    label: "아르트레너 진단",
    sublabel: "Diagnosis",
    color: "#14F1D9",
    glow: "rgba(20,241,217,0.3)",
    description: "비즈니스 모델 캔버스를 함께 설계합니다. 브랜드 전략, 수익 구조, IT 인프라 설계가 이 단계에서 결정됩니다.",
    detail: "BM 설계 → 브랜딩 전략 → 인프라 IA",
  },
  {
    num: "03",
    icon: "ri-hammer-line",
    label: "빌딩 & 런칭",
    sublabel: "Build & Launch",
    color: "#F97316",
    glow: "rgba(249,115,22,0.3)",
    description: "D2C 플랫폼 개발, 브랜드 아이덴티티 제작, 마케팅 캠페인 실행, 첫 팝업 전시 등 실제 비즈니스를 구축합니다.",
    detail: "개발 · BI → 마케팅 → 런칭 이벤트",
  },
  {
    num: "04",
    icon: "ri-line-chart-line",
    label: "스케일업 & 졸업",
    sublabel: "Scale & Phase-out",
    color: "#C084FC",
    glow: "rgba(192,132,252,0.3)",
    description: "목표 수익 달성 후 수익 쉐어 비율이 단계적으로 인하됩니다. 완전한 독립 기업으로 졸업하는 것이 최종 목표입니다.",
    detail: "목표 달성 → 쉐어 인하 → 독립 졸업",
  },
];

export default function ProcessTimeline() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll("[data-timeline-card]");
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = "1";
                (card as HTMLElement).style.transform = "translateY(0)";
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="process"
      className="relative py-28 lg:py-44 px-4 md:px-6 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #06080F 0%, #080B14 100%)" }}
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-1/2 left-0 right-0 h-px opacity-8"
          style={{ background: "linear-gradient(to right, transparent, #A855F7, #14F1D9, #F97316, #C084FC, transparent)" }}
        />
      </div>

      <div ref={sectionRef} className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 lg:mb-24">
          <span className="inline-block text-xs font-display font-medium tracking-widest uppercase text-brand-purple mb-5 px-3 py-1 rounded-full border border-brand-purple/30 bg-brand-purple/5">
            Process Timeline
          </span>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white leading-snug lg:leading-relaxed">
            아이디어에서 기업까지,<br />
            <span className="gradient-text-brand">4단계 빌딩 여정</span>
          </h2>
          <p className="mt-5 text-white/50 font-body text-sm md:text-base max-w-xl mx-auto leading-loose">
            아르트레너의 검증된 프로세스를 통해 당신의 예술이 지속 가능한 기업이 됩니다.
          </p>
        </div>

        {/* Desktop Timeline (horizontal) */}
        <div className="hidden lg:block">
          {/* Node row */}
          <div className="relative flex items-start justify-between">
            {/* Connector line — spans from center of node 1 to center of node 4 */}
            <div
              className="absolute h-0.5"
              style={{
                top: "31px",
                left: "calc(12.5%)",
                right: "calc(12.5%)",
                background:
                  "linear-gradient(to right, rgba(168,85,247,0.5), rgba(20,241,217,0.5), rgba(249,115,22,0.5), rgba(192,132,252,0.5))",
              }}
            />
            {steps.map((step, i) => (
              <div
                key={step.num}
                data-timeline-card
                className="flex-1 flex flex-col items-center px-3"
                style={{
                  opacity: 0,
                  transform: "translateY(20px)",
                  transition: `opacity 0.6s ease ${i * 0.15}s, transform 0.6s ease ${i * 0.15}s`,
                }}
              >
                {/* Node */}
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center relative z-10 mb-8 flex-shrink-0"
                  style={{
                    background: `radial-gradient(circle, ${step.glow} 0%, #06080F 65%)`,
                    border: `2px solid ${step.color}`,
                    boxShadow: `0 0 20px ${step.glow}`,
                  }}
                >
                  <i className={`${step.icon} text-2xl`} style={{ color: step.color }} />
                </div>

                {/* Card */}
                <div
                  className="glass rounded-2xl p-6 lg:p-7 w-full text-center"
                  style={{ border: `1px solid ${step.color}20` }}
                >
                  <span className="text-xs font-display font-bold tracking-widest block" style={{ color: step.color }}>
                    {step.num}
                  </span>
                  <h3 className="font-display text-base font-bold text-white mt-2.5 mb-1.5">{step.label}</h3>
                  <p className="text-xs text-white/40 mb-4 font-body">{step.sublabel}</p>
                  <p className="text-xs text-white/55 font-body leading-loose mb-4">{step.description}</p>
                  <div
                    className="px-3 py-2 rounded-xl text-xs text-white/35 font-body leading-snug"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                  >
                    {step.detail}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile / Tablet Timeline (vertical) */}
        <div className="lg:hidden flex flex-col gap-4">
          {steps.map((step, i) => (
            <div
              key={step.num}
              data-timeline-card
              className="flex gap-4 items-stretch"
              style={{
                opacity: 0,
                transform: "translateY(20px)",
                transition: `opacity 0.6s ease ${i * 0.15}s, transform 0.6s ease ${i * 0.15}s`,
              }}
            >
              {/* Left: node + line */}
              <div className="flex flex-col items-center flex-shrink-0 w-12">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `radial-gradient(circle, ${step.glow} 0%, #06080F 65%)`,
                    border: `2px solid ${step.color}`,
                    boxShadow: `0 0 14px ${step.glow}`,
                  }}
                >
                  <i className={`${step.icon} text-base`} style={{ color: step.color }} />
                </div>
                {i < steps.length - 1 && (
                  <div
                    className="w-px flex-1 mt-2"
                    style={{
                      background: `linear-gradient(to bottom, ${step.color}50, transparent)`,
                      minHeight: "24px",
                    }}
                  />
                )}
              </div>

              {/* Right: content */}
              <div
                className="glass rounded-2xl p-4 flex-1 mb-0"
                style={{ border: `1px solid ${step.color}20` }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="text-xs font-display font-bold tracking-widest"
                    style={{ color: step.color }}
                  >
                    {step.num}
                  </span>
                  <span className="text-xs text-white/30 font-body">·</span>
                  <span className="text-xs text-white/40 font-body">{step.sublabel}</span>
                </div>
                <h3 className="font-display text-sm font-bold text-white mb-1.5">{step.label}</h3>
                <p className="text-xs text-white/55 font-body leading-loose mb-2.5">{step.description}</p>
                <div
                  className="px-2.5 py-1.5 rounded-lg text-xs text-white/35 font-body"
                  style={{ background: "rgba(255,255,255,0.03)" }}
                >
                  {step.detail}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Row */}
        <div className="mt-16 lg:mt-24 text-center">
          <p className="text-white/40 text-sm font-body mb-6">지금 바로 아르트레너와 첫 걸음을 시작하세요</p>
          <a
            href="#canvas"
            className="inline-flex items-center gap-2 px-6 md:px-8 py-3.5 md:py-4 rounded-full font-display font-semibold text-white cursor-pointer whitespace-nowrap transition-all duration-300"
            style={{
              background: "linear-gradient(135deg, #A855F7 0%, #14F1D9 100%)",
              boxShadow: "0 0 30px rgba(168,85,247,0.3), 0 0 60px rgba(20,241,217,0.15)",
            }}
          >
            <i className="ri-play-mini-fill" />
            아르트레너 진단 시작하기
          </a>
        </div>
      </div>
    </section>
  );
}
